import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Star, Check, X, Trash2, Loader2, Image as ImageIcon, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewManager = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved
  const token = localStorage.getItem('admin_token');

  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reviews/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReviews(res.data.reviews || []);
    } catch {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    fetchReviews();
  }, [token, navigate, fetchReviews]);

  const toggleApproval = async (reviewId) => {
    try {
      const res = await axios.put(`${API}/reviews/${reviewId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.approved ? 'Review approved — now visible publicly' : 'Review hidden from public');
      fetchReviews();
    } catch {
      toast.error('Failed to update review');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Review deleted');
      fetchReviews();
    } catch {
      toast.error('Failed to delete review');
    }
  };

  const filtered = reviews.filter(r => {
    if (filter === 'pending') return !r.approved;
    if (filter === 'approved') return r.approved;
    return true;
  });

  const pendingCount = reviews.filter(r => !r.approved).length;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" data-testid="reviews-loading">
      <Loader2 className="animate-spin text-accent" size={32} />
    </div>
  );

  return (
    <div className="min-h-screen bg-background pt-6 pb-16 px-4 sm:px-6 lg:px-8" data-testid="review-manager">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl font-light" data-testid="page-title">Buyer Reviews</h1>
            <p className="font-sans text-xs text-muted-foreground mt-0.5">
              {reviews.length} total · {pendingCount} pending approval
            </p>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6" data-testid="filter-tabs">
          {[
            { key: 'all', label: `All (${reviews.length})` },
            { key: 'pending', label: `Pending (${pendingCount})` },
            { key: 'approved', label: `Approved (${reviews.length - pendingCount})` },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-sans font-medium transition-colors ${
                filter === tab.key
                  ? 'bg-accent text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              data-testid={`filter-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Reviews list */}
        {filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="no-reviews">
            <Star size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {filter === 'pending' ? 'No pending reviews' : 'No reviews yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="reviews-list">
            {filtered.map(review => (
              <div
                key={review.id}
                className={`bg-card rounded-lg border p-5 ${
                  review.approved ? 'border-border' : 'border-amber-200 bg-amber-50/30'
                }`}
                data-testid={`review-item-${review.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="font-sans text-sm font-semibold">{review.customer_name}</span>
                      <span className="text-[10px] text-muted-foreground font-sans">
                        {new Date(review.created_at).toLocaleDateString('en-IN', {
                          year: 'numeric', month: 'short', day: 'numeric',
                        })}
                      </span>
                      {!review.approved && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                          Pending
                        </span>
                      )}
                      {review.approved && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">
                          Approved
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map(i => (
                          <Star key={i} size={13} className={i <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
                        ))}
                      </div>
                      <span className="text-xs text-muted-foreground">for <strong>{review.artwork_title}</strong></span>
                    </div>

                    <p className="font-sans text-sm text-foreground leading-relaxed mb-2">{review.review_text}</p>

                    {review.customer_email && (
                      <p className="font-sans text-[10px] text-muted-foreground">{review.customer_email}</p>
                    )}

                    {review.photo_url && (
                      <div className="mt-3">
                        <img
                          src={`${API.replace('/api', '')}${review.photo_url}`}
                          alt="Review photo"
                          className="rounded-lg max-h-40 object-cover border border-border"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      variant={review.approved ? 'outline' : 'default'}
                      className={review.approved ? '' : 'bg-green-600 hover:bg-green-700'}
                      onClick={() => toggleApproval(review.id)}
                      data-testid={`toggle-approve-${review.id}`}
                    >
                      {review.approved ? (
                        <><EyeOff size={13} className="mr-1" /> Hide</>
                      ) : (
                        <><Check size={13} className="mr-1" /> Approve</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteReview(review.id)}
                      data-testid={`delete-review-${review.id}`}
                    >
                      <Trash2 size={13} className="mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewManager;
